<template>
  <v-expansion-panel>
    <v-expansion-panel-title v-slot="{ expanded }">
      <v-row no-gutters>
        <v-col cols="4" class="text-primary "> Fiche de Préparation </v-col>
        <v-col cols="8" class="text-medium-emphasis">
          <v-fade-transition leave-absolute>
            <span v-if="expanded" key="edit"> Editer la fiche puis sauvegarder </span>
            <div v-else key="summary">
              <div v-if="fichePreparation.account">
                {{ fichePreparation.account.name }}
              </div>
              <div v-if="fichePreparation.laverie">
                / {{ fichePreparation.laverie.name }}
              </div>
            </div>
          </v-fade-transition>
        </v-col>
      </v-row>
    </v-expansion-panel-title>
    <v-expansion-panel-text>
      <v-row no-gutters>
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-autocomplete v-model="fichePreparation.account" :items="accounts" item-title="name" return-object
            label="Siège"></v-autocomplete>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3">
          <br />
          Choisissez le compte siège
        </v-col>
      </v-row>
      <v-row v-if="fichePreparation.account" no-gutters>
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-autocomplete v-model="fichePreparation.laverie" :items="laveries" item-title="name" return-object
            label="Laverie"></v-autocomplete>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3">
          <br />
          Choisissez la laverie
        </v-col>
      </v-row>
      <v-row v-if="fichePreparation.laverie" no-gutters>
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-text-field v-model="preparedForModel" type="date">
            <template #label>
              Préparer pour<span class="text-red"><strong> *</strong></span>
            </template>
          </v-text-field>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3">
          <br />
          Choisissez la date
        </v-col>
      </v-row>
      <v-row v-if="fichePreparation.preparedFor" no-gutters>
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-autocomplete v-model="fichePreparation.preparedBy" :items="users" item-title="fullName" return-object
            label="Préparé par"></v-autocomplete>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3">
          <br />
          Choisissez le préparateur
        </v-col>
      </v-row>
      <v-row v-if="
        fichePreparation.preparedBy &&
        fichePreparation.preparedBy.lastname == 'personne'
      " no-gutters>
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-text-field name="autreUserLastname" v-model="fichePreparation.preparedBy.name" 
            label="Précisez">
          </v-text-field>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3"> </v-col>
      </v-row>

      <v-row v-if="fichePreparation.preparedBy" no-gutters>
        <v-spacer></v-spacer>
        <v-col cols="6">
          <v-autocomplete v-model="fichePreparation.products" :items="products" item-title="title" return-object
            label="Produits" multiple></v-autocomplete>
        </v-col>
        <v-divider vertical class="mx-4"></v-divider>
        <v-col cols="3">
          <br />
          Selectionnez les produits
        </v-col>
      </v-row>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="onCancel" text color="red"> Cancel </v-btn>
        <generate-doc-btn :filePath="fichePreparationTemplatePath" :fileName="fichePreparationFileName"
          :dataSet="fichePreparationGen" @success="onDocumentGeneratedSuccessfully">Save</generate-doc-btn>
      </v-card-actions>
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import GenerateDocBtn from "@/components/GenerateDocBtn.vue";
import type { FichePreparation, Product, Task } from "@/types";
import { flattenFichePreparation } from "@/types";
import type { Account } from "@/types";
import type { Laverie } from "@/types";
import type { User } from "@/types";
import { TaskStatus, TaskType } from "@/types/enum-type";
import { useAppStore } from "@/store/app";
import { getLaveriesByAccountId } from "@/utils/laverie-api";
import { postTask } from "@/utils/task-api";

interface Props {
  accounts: Account[];
  users: User[];
  products: (Product | { type: 'subheader'; title: string })[];
}

const props = defineProps<Props>();
const appStore = useAppStore();

const fichePreparation = ref<FichePreparation>({} as FichePreparation);
const laveries = ref<Laverie[]>([]);

const fichePreparationGen = computed(() =>
  flattenFichePreparation(fichePreparation.value)
);

const fichePreparationTemplatePath = "templates/FichePreparationTemplate.docx";

const fichePreparationFileName = computed(() => {
  if (fichePreparation.value.laverie?.name) {
    return `FicheDePreparation_${fichePreparation.value.laverie.name}`;
  }
  return "FicheDePreparation";
});

const preparedForModel = computed({
  get() {
    const d = fichePreparation.value.preparedFor;
    if (!d) return "";
    return d.toISOString().slice(0, 10);
  },
  set(value: string) {
    if (!value) {
      fichePreparation.value.preparedFor = null;
      return;
    }

    // timezone-safe parse
    const [y, m, d] = value.split("-").map(Number);
    fichePreparation.value.preparedFor = new Date(y, m - 1, d);
  },
});

watch(
  () => fichePreparation.value.account,
  async (selectedAccount) => {
    if (!selectedAccount) return;

    // Reset selected laverie
    fichePreparation.value.laverie = null;
    laveries.value = [];

    await fetchLaveries(selectedAccount.id);
  }
);

const fetchLaveries = async (accountId: string) => {
  try {
    laveries.value = await getLaveriesByAccountId(accountId);
  } catch (error) {
    console.error("Error fetching laveries:", error);
    laveries.value = [];
  }
};

const onCancel = () => {
  fichePreparation.value = {} as FichePreparation;
  laveries.value = [];
};

const onDocumentGeneratedSuccessfully = async () => {
  if (!fichePreparation.value.laverie?.id) {
    appStore.sendErrorNotice("Laverie is required to create a preparation task.");
    return;
  }

  const task: Task = {
    name: fichePreparation.value.laverie.name ?? "Preparation",
    subject: `Preparation ${fichePreparation.value.laverie.name ?? ""}`.trim(),
    taskType: TaskType.PREPARATION,
    taskStatus: TaskStatus.A_FAIRE,
    observation: "",
    numeroDeContrat: fichePreparation.value.laverie.numeroDeContrat?.toString() ?? "",
    toBeDone: true,
    laverieId: fichePreparation.value.laverie.id,
    subtasks: [],
  } as Task;

  try {
    await postTask(task);
    appStore.sendSuccessNotice("Preparation task has been created.");
  } catch (error) {
    // Error notice is already handled globally; this catches to avoid unhandled rejections.
  }
};
</script>
